import { BonusContext, MessagingConstants, RoundMessage, RoundMessageType } from '@cgs/common';
import { BonusResult, BonusRound, RoundButton } from '@cgs/data-access';
import {
  BonusRoundWithRules,
  IRoundRules,
  RulePropertyGenerator,
  RuleTypeLogic,
} from '../../bonus_game_with_rules';
import { IRoundMessageResolver } from '../../interfaces/i_round_message_resolver';
import { DefaultRoundResolver } from './default_round_resolver';

export interface IMessageTypeLogic {
  name: string;
  failTypes?: string[];
  winTypes?: string[];
  collectTarget?: number;
  collectType?: string;
}

export class GenericRoundResolver extends DefaultRoundResolver implements IRoundMessageResolver {
  message(
    bonusContext: BonusContext,
    bonusRound: BonusRoundWithRules,
    bonusResult: BonusResult | null
  ): RoundMessage {
    const rules = bonusRound.rules;
    const messageType = this.determineMessageType(bonusRound, rules);

    return RoundMessage.fromProperties(
      messageType,
      this.properties(bonusContext, bonusRound, bonusResult)
    );
  }

  properties(
    bonusContext: BonusContext,
    bonusRound: BonusRoundWithRules,
    bonusResult: BonusResult | null
  ): Map<string, any> {
    const defaultResult = {
      bonusWin: 0,
      totalWin: 0,
      multiplier: 1,
      bet: 0,
      lines: 0,
      winName: '',
      betCalculation: '',
      additionWin: 0,
      specGroups: null,
      winLines: null,
    };
    const properties = super.properties(bonusContext, bonusRound, bonusResult || defaultResult);
    const rules = bonusRound.rules;

    if (rules?.propertyGenerators) {
      for (const generator of rules.propertyGenerators) {
        this.generateProperties(generator, bonusRound, properties);
      }
    }

    return properties;
  }

  private determineMessageType(
    bonusRound: BonusRoundWithRules,
    rules: IRoundRules | null
  ): RoundMessageType {
    if (!rules?.messageTypeLogic) {
      return this.getDefaultMessageType(bonusRound);
    }

    const logic = rules.messageTypeLogic;
    const selectedButtons = bonusRound.selectedButtons || [];

    if (selectedButtons.length === 0) {
      return RoundMessageType.None;
    }

    const lastSelectedButton = selectedButtons[selectedButtons.length - 1];

    switch (logic.name) {
      case RuleTypeLogic.WinLose:
        return this.handleWinLoseLogic(lastSelectedButton, logic);
      //TODO: add other message types

      default:
        return this.getDefaultMessageType(bonusRound);
    }
  }

  private handleWinLoseLogic(lastButton: RoundButton, logic: IMessageTypeLogic): RoundMessageType {
    const winTypes = logic.winTypes || [];
    const failTypes = logic.failTypes || [];

    if (failTypes.includes(lastButton.type)) {
      return RoundMessageType.Lose;
    }
    if (winTypes.includes(lastButton.type)) {
      return RoundMessageType.Win;
    }

    return RoundMessageType.None;
  }

  private getDefaultMessageType(bonusRound: BonusRound): RoundMessageType {
    return bonusRound?.selectedButtons && bonusRound.selectedButtons.length > 0
      ? RoundMessageType.Win
      : RoundMessageType.None;
  }

  private generateProperties(
    generator: string,
    bonusRound: BonusRoundWithRules,
    properties: Map<string, any>
  ): void {
    switch (generator) {
      case RulePropertyGenerator.WinLoseIndexes:
        this.generateWinLoseIndexes(bonusRound, properties);
        break;
      //TODO: add other generators

      default:
        // Unknown generator, skip
        break;
    }
  }

  private generateWinLoseIndexes(
    bonusRound: BonusRoundWithRules,
    properties: Map<string, any>
  ): void {
    const selectedButtons = bonusRound.selectedButtons || [];
    const notSelectedButtons = bonusRound.notSelectedButtons || [];

    // Extract rules to determine what constitutes win/lose
    const rules = bonusRound.rules;
    const failTypes = rules?.messageTypeLogic?.failTypes || [];

    const winIndexes: number[] = [];
    const loseIndexes: number[] = [];

    // Process selected buttons
    selectedButtons.forEach((button) => {
      if (failTypes.includes(button.type)) {
        loseIndexes.push(button.index);
      } else {
        winIndexes.push(button.index);
      }
    });

    // Process not selected buttons
    notSelectedButtons.forEach((button) => {
      if (failTypes.includes(button.type)) {
        loseIndexes.push(button.index);
      } else {
        winIndexes.push(button.index);
      }
    });

    properties.set(MessagingConstants.winIndexes, winIndexes);
    properties.set(MessagingConstants.loseIndexes, loseIndexes);
  }
}
